import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import { analytics } from '@/shared/services/analytics.service';
import { ConfigService } from '@/shared/services/config.service';
import type { ReactNode } from 'react';
import { useAuth } from '../../app/core/auth/application/hooks/useAuth';

// Read at connect time, not at import: on an organization's custom domain the
// socket host is resolved at runtime (runtime-endpoints).
const socketUrl = (): string =>
  ConfigService.getInstance().get('socketUrl') || 'http://localhost:3000';

export interface SocketContextProps {
  socket: Socket | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  reconnect: () => void;
}

const SocketContext = createContext<SocketContextProps | null>(null);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { token } = useAuth();

  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Always points to the live socket so `reconnect` doesn't need the token
  // as a dependency (avoids stale closure).
  const socketRef = useRef<Socket | null>(null);

  const reconnect = useCallback(() => {
    const s = socketRef.current;
    if (s && !s.connected) {
      setError(null);
      s.connect();
    }
  }, []);

  useEffect(() => {
    if (!token) {
      console.log('[Socket] No token — skipping connection');
      setIsConnected(false);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const url = socketUrl();
    console.log('[Socket] Connecting →', url);

    const s = io(url, {
      auth: { Authorization: `Bearer ${token}` },
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 5_000,
      timeout: 10_000,
    });

    socketRef.current = s;
    setSocket(s);

    let connectCount = 0;
    const onConnect = () => {
      analytics.capture('socket:connected', { is_reconnect: connectCount > 0 });
      connectCount += 1;
      setIsConnected(true);
      setIsLoading(false);
      setError(null);
      console.log('[Socket] ✅ Connected — id:', s.id);
    };

    const onDisconnect = (reason: string) => {
      analytics.capture('socket:disconnected', { reason });
      setIsConnected(false);
      setIsLoading(false);
      console.warn('[Socket] ⚠️ Disconnected — reason:', reason);
      // 'io server disconnect' means the server kicked us — trigger manual reconnect.
      if (reason === 'io server disconnect') s.connect();
    };

    const onConnectError = (err: Error) => {
      setIsLoading(false);
      const isAuthError =
        err.message.includes('unauthorized') ||
        err.message.includes('authentication') ||
        err.message.includes('jwt');
      setError(isAuthError ? `Authentication failed: ${err.message}` : err.message);
      analytics.capture('socket:connect_failed', { is_auth_error: isAuthError, is_final: false });
      if (isAuthError) s.disconnect();
      console.error('[Socket] ❌ Connect error:', err.message);
    };

    // reconnect_attempt / reconnect_failed / reconnect_error are Manager events
    // in socket.io-client v4 — must be registered on `s.io`, not on `s`.
    const onReconnectAttempt = (n: number) => {
      console.log(`[Socket] 🔄 Reconnect attempt ${n}`);
    };

    const onReconnectFailed = () => {
      analytics.capture('socket:connect_failed', { is_auth_error: false, is_final: true });
      setError('Failed to reconnect after multiple attempts');
      setIsLoading(false);
      console.error('[Socket] ❌ Reconnect failed — giving up');
    };

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('connect_error', onConnectError);
    s.io.on('reconnect_attempt', onReconnectAttempt);
    s.io.on('reconnect_failed', onReconnectFailed);

    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off('connect_error', onConnectError);
      s.io.off('reconnect_attempt', onReconnectAttempt);
      s.io.off('reconnect_failed', onReconnectFailed);
      s.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    };
  }, [token]);

  const value = useMemo(
    () => ({ socket, isConnected, isLoading, error, reconnect }),
    [socket, isConnected, isLoading, error, reconnect],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within a SocketProvider');
  return ctx;
};

/**
 * The socket when there is one, `null` when there is not.
 *
 * `useSocket` throws outside the provider, which is right for a feature that
 * cannot work without it. A feature that only uses the socket to avoid polling
 * still has to render in Storybook, in tests and under the mock backend, where
 * no provider is mounted — and there the honest answer is "no socket", not a
 * crash.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalSocket = (): SocketContextProps | null => useContext(SocketContext);
